import { AnimatePresence, motion, MotionConfig } from "motion/react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import React from "react";
import { decodeFA } from "../utils/decodeFA.js";
import { cn } from "../utils/cn.js";

// takes an HTMLElement instead of react image so it works with rich text

type ImageZoomPropsNew = {
  src: string;
  children: React.ReactElement<HTMLImageElement>;
  caption?: string;
  buttonClassName?: string;
};

const ImageZoom = ({
  src,
  children,
  caption,
  buttonClassName = ""
}: ImageZoomPropsNew) => {
  const [smallImageLoaded, setSmallImageLoaded] = useState(false);
  const [zoomState, setZoomState] = useState<"ready" | "loading" | "zoomed">(
    "ready"
  );
  const [error, setError] = useState<null | string>(null);
  const smallImageRef = useRef<HTMLButtonElement | null>(null);

  const child = React.Children.only(children);
  const imageSrc = child.props.src;

  useEffect(() => {
    const smallImage = new window.Image();
    smallImage.onload = () => {
      setSmallImageLoaded(true);
      setError(null);
    };
    smallImage.onerror = (e) => {
      setSmallImageLoaded(false);
      setError(
        `Failed to load image: ${typeof e === "string" ? e : JSON.stringify(e)}`
      );
    };
    smallImage.src = imageSrc;
  }, [imageSrc]);

  useEffect(() => {
    if (zoomState === "loading") {
      const bigImage = new window.Image();
      bigImage.onload = () => {
        setZoomState("zoomed");
        setError(null);
      };
      bigImage.onerror = (e) => {
        setZoomState("ready");
        setError(
          `Failed to load image: ${
            typeof e === "string" ? e : JSON.stringify(e)
          }`
        );
      };
      bigImage.src = src;
    }
  }, [zoomState, src]);

  if (error)
    return (
      <div className="dark:bg-mono-900 flex aspect-3/2 py-6 w-full items-center justify-center rounded-md   border border-red-400 bg-red-200 text-red-800 dark:text-red-400">
        <div className="flex items-center gap-4">
          <i className={`fa-regular size-8 ${decodeFA("image-slash")}`} />
          <div className="">
            <p className="my-0! text-sm font-medium">Error loading image</p>
            <p className="my-0! text-xs">{JSON.stringify(error)}</p>
          </div>
        </div>
      </div>
    );
  if (!smallImageLoaded) {
    return (
      <div className="bg-mono-200 dark:bg-mono-900  aspect-3/2 w-full animate-pulse" />
    );
  }
  const imgRef = smallImageRef.current?.querySelector("img");
  const bounds = imgRef?.getBoundingClientRect();

  return (
    <>
      <MotionConfig
        transition={{ type: "spring", bounce: 0, duration: 0.45, delay: 0 }}
      >
        <AnimatePresence>
          {zoomState === "zoomed" && (
            <DialogPrimitive.Root
              defaultOpen={true}
              open={true}
              onOpenChange={(open) => {
                if (!open) {
                  setZoomState("ready");
                }
              }}
            >
              <DialogPrimitive.Portal>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className={
                    "dark:bg-mono-950/30 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-white/30 backdrop-blur-xs"
                  }
                />
                <DialogPrimitive.Content
                  className={`fixed inset-0 z-50 bg-transparent`}
                  style={{ pointerEvents: "none" }}
                  aria-describedby={undefined}
                >
                  <>
                    <VisuallyHidden.Root>
                      <VisuallyHidden.Root></VisuallyHidden.Root>
                      <DialogPrimitive.Title>
                        Zoomed image
                      </DialogPrimitive.Title>
                    </VisuallyHidden.Root>

                    {bounds ? (
                      <Image
                        bounds={bounds}
                        src={src}
                        caption={caption}
                        onClose={() => setZoomState("ready")}
                      />
                    ) : (
                      <div>Error mesuring image</div>
                    )}
                  </>
                </DialogPrimitive.Content>
              </DialogPrimitive.Portal>
            </DialogPrimitive.Root>
          )}
        </AnimatePresence>
      </MotionConfig>
      <button
        type="button"
        className={cn(
          "block w-full cursor-zoom-in border-0 bg-transparent p-0",
          zoomState === "loading" && "animate-pulse",
          buttonClassName
        )}
        ref={smallImageRef}
        onClick={() => setZoomState("loading")}
      >
        {children}
      </button>
    </>
  );
};

type ImageProps = {
  bounds: {
    x: number;
    y: number;
    width: number;
    height: number;
    top: number;
    left: number;
  };
  src: string;
  caption?: string;
  onClose?: () => void;
  onImageLoaded?: () => void;
};

const Image = ({
  bounds,
  src,
  caption,
  onClose,
  onImageLoaded
}: ImageProps) => {
  const [image, setImage] = useState<HTMLImageElement | false>(false);

  useEffect(() => {
    const bigImage = new window.Image();
    bigImage.src = src;
    bigImage.onload = () => {
      setImage(bigImage);
      onImageLoaded?.();
    };
  }, [src, onImageLoaded]);
  if (!image) return null;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  // Center of the viewport
  const viewportCenterX = viewportWidth / 2;
  const viewportCenterY = viewportHeight / 2;

  const scaleFactor = Math.max(
    bounds.width / Math.min(image.naturalWidth, viewportWidth),
    bounds.height / Math.min(image.naturalHeight, viewportHeight)
  );

  const scaleFactorLarge = Math.min(
    Math.max(viewportWidth, viewportHeight),
    0.95
  );

  const aspectRatio = image.naturalWidth / image.naturalHeight;
  let width = Math.min(image.naturalWidth, viewportWidth);
  let height = width / aspectRatio;

  // If calculated height is larger than viewport height, adjust height and width
  if (height > viewportHeight) {
    height = viewportHeight;
    width = height * aspectRatio;
  }
  return (
    <motion.div
      className={` absolute items-center justify-center text-white`}
      initial={{
        y: `${bounds.top}px`,
        x: `${bounds.left}px`,
        scale: scaleFactor
      }}
      animate={{
        y: `${
          viewportCenterY -
          height / 2 +
          viewportCenterY * (1 - scaleFactorLarge)
        }px`,
        x: `${
          viewportCenterX - width / 2 + viewportCenterX * (1 - scaleFactorLarge)
        }px`,
        scale: scaleFactorLarge
      }}
      exit={{
        y: `${bounds.top}px`,
        x: `${bounds.left}px`,
        scale: scaleFactor
      }}
      style={{
        width: `${width}px`,
        height: `${height}px`,
        transformOrigin: "top left",
        pointerEvents: "auto"
      }}
    >
      <motion.div
        className={`dark:bg-mono-950 dark:border-mono-700/50 absolute inset-0 rounded-lg border bg-white  shadow-lg`}
        initial={{ opacity: 0, scale: 1 }}
        animate={{ opacity: 1, scale: 1.02 }}
        exit={{ opacity: 0, scale: 1 }}
      />
      <a href={src} target="_blank" rel="noopener noreferrer">
        <motion.img
          className={`relative max-h-full max-w-full rounded-md`}
          src={image.src}
        />
      </a>
      {caption && (
        <motion.div
          className="relative truncate px-2.5 py-2 text-sm font-medium text-mono-900 dark:text-mono-100"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {caption}
        </motion.div>
      )}
      <motion.button
        onClick={() => {
          onClose?.();
        }}
        initial={{ opacity: 0, scale: 1 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 1 }}
        className={`text-mono-800 dark:bg-mono-950 dark:text-mono-200 absolute right-0 top-0 z-10 rounded-bl-md bg-white`}
      >
        <XMarkIcon className={`h-6 w-6`} />
      </motion.button>
    </motion.div>
  );
};

export default ImageZoom;
